import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { AppShell } from '@components/layout/AppShell';
import { AccountDetailPage } from '@pages/AccountDetailPage';
import { useCurrentUser } from '@hooks/useAuth';
import { AccountsPage } from '@pages/AccountsPage';
import { CadencePage } from '@pages/CadencePage';
import { DashboardPage } from '@pages/DashboardPage';
import { AppHealthPage } from '@pages/AppHealthPage';
import { ApolloProspectingPage } from '@pages/ApolloProspectingPage';
import { LeadDetailPage } from '@pages/LeadDetailPage';
import { LeadsPage } from '@pages/LeadsPage';
import { LoginPage } from '@pages/LoginPage';
import { OpportunitiesPage } from '@pages/OpportunitiesPage';
import { SettingsPage } from '@pages/SettingsPage';
import { TemplatesPage } from '@pages/TemplatesPage';
import { PromotionsPage } from '@pages/PromotionsPage';
import { FeaturesPage } from '@pages/FeaturesPage';
import { useSwitchTenant } from '@hooks/useAuth';

const CRM_TENANT_SLUG = 'ez-flow';

function LoadingScreen() {
  return (
    <div className="center-stage">
      <div className="loading-panel">
        <p className="hero-kicker">CRM Boot</p>
        <h1>Connecting to EZ Flow CRM</h1>
        <p>Checking your session and active tenant before loading founder workflows.</p>
      </div>
    </div>
  );
}

function TenantRequiredScreen() {
  return (
    <div className="center-stage">
      <div className="loading-panel loading-panel-warning">
        <AlertCircle size={28} />
        <h1>Active Tenant Required</h1>
        <p>
          Your account is authenticated, but no active tenant is attached to this session yet. Switch tenants from the
          shared product flow, or complete onboarding for your first tenant.
        </p>
      </div>
    </div>
  );
}

function CRMAccessRequiredScreen({ ezFlowTenantId }: { ezFlowTenantId?: string }) {
  const switchTenant = useSwitchTenant();

  return (
    <div className="center-stage">
      <div className="loading-panel loading-panel-warning">
        <AlertCircle size={28} />
        <h1>EZ Flow Tenant Required</h1>
        <p>
          This CRM is restricted to the internal EZ Flow workspace. Switch your active tenant to continue.
        </p>
        {ezFlowTenantId ? (
          <button
            className="button-primary"
            type="button"
            onClick={() => switchTenant.mutate(ezFlowTenantId)}
            disabled={switchTenant.isPending}
          >
            {switchTenant.isPending ? 'Switching...' : 'Switch to EZ Flow'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function App() {
  const location = useLocation();
  const { data: currentUser, isLoading } = useCurrentUser();
  const isAuthenticated = Boolean(currentUser?.id);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />} />
      </Routes>
    );
  }

  if (!currentUser?.active_tenant_id) {
    return <TenantRequiredScreen />;
  }

  if (currentUser.active_tenant?.slug !== CRM_TENANT_SLUG) {
    const ezFlowTenantId = currentUser.available_tenants.find((tenant) => tenant.slug === CRM_TENANT_SLUG)?.id;
    return <CRMAccessRequiredScreen ezFlowTenantId={ezFlowTenantId} />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route element={<AppShell />}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/app-health" element={<AppHealthPage />} />
        <Route path="/accounts" element={<AccountsPage />} />
        <Route path="/accounts/new" element={<AccountDetailPage isCreating />} />
        <Route path="/accounts/:accountId" element={<AccountDetailPage />} />
        <Route path="/apollo" element={<ApolloProspectingPage />} />
        <Route path="/leads" element={<LeadsPage />} />
        <Route path="/leads/new" element={<LeadDetailPage isCreating />} />
        <Route path="/leads/:leadId" element={<LeadDetailPage />} />
        <Route path="/opportunities" element={<OpportunitiesPage />} />
        <Route path="/cadence" element={<CadencePage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/promotions" element={<PromotionsPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
